import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, AlertCircle, CheckCircle2, Lock, LockOpen, RefreshCw } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { useAuth } from '../../../../context/AuthContext';
import { Permissions } from '../../../../constants/Permissions';
import { outletService } from '../../../../services/outletService';
import {
  businessDayService,
  BusinessDayDTO,
  DayEndPreviewDTO,
} from '../../../../services/businessDayService';
import { parseApiError } from '../../../../utils/errorUtils';
import './TillPage.css';

interface OutletOption { id: number; name: string; }

/**
 * Day End console — one outlet's trading day: what it has taken so far, which tills are
 * still open, what is blocking a close, and the archive of past Z-reports.
 */
const DayEndPage: React.FC = () => {
  const { t }        = useTranslation();
  const navigate     = useNavigate();
  const { hasPermission } = useAuth();

  const canExecute = hasPermission(Permissions.DayEnd.Execute);
  const canReopen  = hasPermission(Permissions.DayEnd.Reopen);

  const [outlets,    setOutlets]    = useState<OutletOption[]>([]);
  const [outletId,   setOutletId]   = useState<number>(0);
  const [preview,    setPreview]    = useState<DayEndPreviewDTO | null>(null);
  const [history,    setHistory]    = useState<BusinessDayDTO[]>([]);
  const [notes,      setNotes]      = useState('');

  const [loading,    setLoading]    = useState(true);
  const [working,    setWorking]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [success,    setSuccess]    = useState<string | null>(null);

  const fmt = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtDate = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

  const fmtTime = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : '—';

  // ── Load outlets once ─────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res  = await outletService.getAll(1, 200);
        const list = ((res.data?.data as any)?.data ?? res.data?.data ?? []) as any[];
        const opts = list.map((o) => ({ id: o.id, name: o.name }));
        setOutlets(opts);
        if (opts.length > 0) setOutletId(opts[0].id);
        else setLoading(false);
      } catch (err) {
        setError(parseApiError(err, t('dayEnd.loadFailed')).message);
        setLoading(false);
      }
    })();
  }, [t]);

  // ── Load the selected outlet's day + history ──────────────────────────────
  const load = useCallback(async (id: number) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [previewRes, historyRes] = await Promise.all([
        businessDayService.getPreview(id),
        businessDayService.getHistory(id, 1, 30),
      ]);
      setPreview(previewRes.data?.data ?? null);
      setHistory(historyRes.data?.data ?? []);
    } catch (err) {
      setError(parseApiError(err, t('dayEnd.loadFailed')).message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { if (outletId) load(outletId); }, [outletId, load]);

  const day      = preview?.businessDay ?? null;
  const blockers = preview?.blockers ?? [];
  const tills    = preview?.tillSessions ?? [];
  const canClose = !!preview?.canClose;

  const hasNoOpenDay = useMemo(
    () => blockers.some((b) => b.code === 'NoOpenDay'),
    [blockers],
  );

  // ── Actions ───────────────────────────────────────────────────────────────

  const runAction = async (fn: () => Promise<unknown>, okMessage: string, fallback: string) => {
    setWorking(true);
    setError(null);
    setSuccess(null);
    try {
      await fn();
      setSuccess(okMessage);
      await load(outletId);
    } catch (err) {
      setError(parseApiError(err, t(fallback)).message);
    } finally {
      setWorking(false);
    }
  };

  const handleOpen = () =>
    runAction(
      () => businessDayService.open(outletId),
      t('dayEnd.openedSuccess'),
      'dayEnd.openFailed',
    );

  const handleClose = () =>
    runAction(
      async () => {
        await businessDayService.close(outletId, notes || undefined);
        setNotes('');
      },
      t('dayEnd.closedSuccess'),
      'dayEnd.failed',
    );

  const handleReopen = (target: BusinessDayDTO) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(t('dayEnd.reopenPrompt'));
    if (!reason || reason.trim().length < 5) return;
    return runAction(
      () => businessDayService.reopen(target.id, reason.trim()),
      t('dayEnd.reopenedSuccess'),
      'dayEnd.reopenFailed',
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="ims-page till-page">

        {/* Top bar */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={13} strokeWidth={2.5} />
              {t('common.back')}
            </button>
            <div>
              <h1 className="ims-page-title">{t('dayEnd.title')}</h1>
              <p className="ims-page-subtitle">{t('dayEnd.subtitle')}</p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => load(outletId)}
              disabled={loading || working || !outletId}
            >
              <RefreshCw size={14} strokeWidth={2} />
              {t('common.refresh')}
            </button>
          </div>
        </div>

        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            <AlertCircle size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="ims-alert ims-alert-success">
            <CheckCircle2 size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{success}</span>
          </div>
        )}

        {/* Outlet selector — hidden when the company has a single site */}
        {outlets.length > 1 && (
          <div className="ims-card" style={{ marginBottom: 16 }}>
            <div className="ims-card-body">
              <div className="ims-form-group" style={{ marginBottom: 0, maxWidth: 360 }}>
                <label className="ims-form-label" htmlFor="day-end-outlet">
                  {t('dayEnd.outlet')}
                </label>
                <select
                  id="day-end-outlet"
                  className="ims-form-control"
                  value={outletId}
                  onChange={(e) => setOutletId(Number(e.target.value))}
                  disabled={working}
                >
                  {outlets.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <div className="ims-spinner" />
          </div>
        ) : outlets.length === 0 ? (
          <div className="ims-card"><div className="ims-card-body">{t('dayEnd.noOutlets')}</div></div>
        ) : hasNoOpenDay ? (
          /* ── No open day ── */
          <div className="ims-card">
            <div className="ims-card-body" style={{ textAlign: 'center', padding: 32 }}>
              <LockOpen size={32} strokeWidth={1.5} style={{ marginBottom: 12, opacity: 0.6 }} />
              <h3 style={{ marginBottom: 6 }}>{t('dayEnd.noOpenDay')}</h3>
              <p style={{ color: 'var(--ims-text-muted)', marginBottom: 20 }}>
                {t('dayEnd.noOpenDayHint')}
              </p>
              {canExecute && (
                <button
                  type="button"
                  className="ims-btn ims-btn-primary"
                  onClick={handleOpen}
                  disabled={working}
                >
                  {working ? <span className="ims-btn-spinner" /> : <LockOpen size={14} strokeWidth={2} />}
                  {t('dayEnd.openDay')}
                </button>
              )}
            </div>
          </div>
        ) : day && (
          <div className="ims-create-body">

            {/* ── LEFT: the day + its totals ── */}
            <div className="ims-panel-left" style={{ minWidth: 300 }}>
              <div className="ims-card">
                <div className="ims-card-header">
                  <h2 className="ims-card-title">{t('dayEnd.currentDay')}</h2>
                </div>
                <div className="ims-card-body">
                  <div style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: 4 }}>
                    {fmtDate(day.businessDate)}
                  </div>
                  <div style={{ color: 'var(--ims-text-muted)', fontSize: '0.8rem', marginBottom: 16 }}>
                    {day.outletName} · {t('dayEnd.openedAt')} {fmtTime(day.openedAt)}
                    {day.openedByName ? ` · ${day.openedByName}` : ''}
                  </div>

                  <dl style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px 12px', margin: 0 }}>
                    <dt>{t('dayEnd.grossSales')}</dt>    <dd style={{ textAlign: 'right' }}>{fmt(day.grossSales)}</dd>
                    <dt>{t('dayEnd.discounts')}</dt>     <dd style={{ textAlign: 'right' }}>-{fmt(day.discountTotal)}</dd>
                    <dt>{t('dayEnd.tax')}</dt>           <dd style={{ textAlign: 'right' }}>{fmt(day.taxTotal)}</dd>
                    <dt style={{ fontWeight: 700 }}>{t('dayEnd.netSales')}</dt>
                    <dd style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(day.netSales)}</dd>
                    <dt>{t('dayEnd.invoiceCount')}</dt>  <dd style={{ textAlign: 'right' }}>{day.invoiceCount}</dd>
                    <dt>{t('dayEnd.cashVariance')}</dt>
                    <dd style={{
                      textAlign: 'right',
                      color: day.cashVariance === 0 ? undefined : 'var(--ims-red-text)',
                    }}>
                      {fmt(day.cashVariance)}
                    </dd>
                  </dl>

                  {day.tenderBreakdown.length > 0 && (
                    <>
                      <h4 style={{ marginTop: 20, marginBottom: 8, fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--ims-text-muted)' }}>
                        {t('dayEnd.tenders')}
                      </h4>
                      <dl style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 12px', margin: 0 }}>
                        {day.tenderBreakdown.map((tender) => (
                          <React.Fragment key={tender.method}>
                            <dt>{tender.method} <span style={{ color: 'var(--ims-text-muted)' }}>({tender.count})</span></dt>
                            <dd style={{ textAlign: 'right' }}>{fmt(tender.amount)}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ── CENTER: checklist + tills ── */}
            <div className="ims-panel-center">
              <div className="ims-card">
                <div className="ims-card-header">
                  <h2 className="ims-card-title">{t('dayEnd.checklist')}</h2>
                </div>
                <div className="ims-card-body">
                  {blockers.length === 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ims-green-text, green)' }}>
                      <CheckCircle2 size={18} strokeWidth={2} />
                      {t('dayEnd.readyToClose')}
                    </div>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
                      {blockers.map((blocker, i) => (
                        <li
                          key={`${blocker.code}-${blocker.referenceId ?? i}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                        >
                          <AlertCircle size={16} strokeWidth={2} style={{ color: 'var(--ims-red-text)', flexShrink: 0 }} />
                          <span>{blocker.description}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="ims-card" style={{ marginTop: 16 }}>
                <div className="ims-card-header">
                  <h2 className="ims-card-title">{t('dayEnd.tillSessions')}</h2>
                </div>
                <div className="ims-card-body">
                  {tills.length === 0 ? (
                    <span style={{ color: 'var(--ims-text-muted)' }}>{t('dayEnd.noTills')}</span>
                  ) : (
                    <div className="ims-table-scroll">
                      <table className="ims-table">
                        <thead>
                          <tr>
                            <th className="ims-th">{t('dayEnd.session')}</th>
                            <th className="ims-th">{t('dayEnd.cashier')}</th>
                            <th className="ims-th">{t('common.status')}</th>
                            <th className="ims-th" style={{ textAlign: 'right' }}>{t('dayEnd.variance')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tills.map((till) => (
                            <tr key={till.id} className="ims-tr">
                              <td className="ims-td">{till.sessionNumber}</td>
                              <td className="ims-td">{till.cashierName}</td>
                              <td className="ims-td">
                                <span className="ims-badge">{till.status}</span>
                              </td>
                              <td className="ims-td" style={{ textAlign: 'right' }}>
                                {till.variance == null ? '—' : fmt(till.variance)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Close action */}
              {canExecute && (
                <div className="ims-card" style={{ marginTop: 16 }}>
                  <div className="ims-card-body">
                    <div className="ims-form-group">
                      <label className="ims-form-label" htmlFor="day-end-notes">
                        {t('common.notes')}
                      </label>
                      <textarea
                        id="day-end-notes"
                        className="ims-form-control ims-textarea"
                        rows={2}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t('dayEnd.notesPlaceholder')}
                      />
                    </div>
                    <button
                      type="button"
                      className="ims-btn ims-btn-primary"
                      onClick={handleClose}
                      disabled={!canClose || working}
                      title={canClose ? undefined : t('dayEnd.resolveBlockersFirst')}
                    >
                      {working ? <span className="ims-btn-spinner" /> : <Lock size={14} strokeWidth={2} />}
                      {t('dayEnd.closeDay')}
                    </button>
                    {!canClose && (
                      <span style={{ marginInlineStart: 12, color: 'var(--ims-text-muted)', fontSize: '0.8rem' }}>
                        {t('dayEnd.resolveBlockersFirst')}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── RIGHT: Z-report archive ── */}
            <div className="ims-panel-right">
              <div className="ims-card">
                <div className="ims-card-header">
                  <h3 className="ims-card-title">{t('dayEnd.history')}</h3>
                </div>
                <div className="ims-card-body">
                  {history.length === 0 ? (
                    <span style={{ color: 'var(--ims-text-muted)' }}>{t('dayEnd.noHistory')}</span>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
                      {history.map((closed) => (
                        <li key={closed.id} style={{ borderBottom: '1px solid var(--ims-border)', paddingBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <strong>{closed.businessDate}</strong>
                            <span className="ims-badge">Z{closed.dayEndNumber}</span>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--ims-text-muted)' }}>
                            {t('dayEnd.netSales')}: {fmt(closed.netSales)} · {closed.invoiceCount} {t('dayEnd.invoices')}
                          </div>
                          {closed.reopenReason && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--ims-red-text)' }}>
                              {t('dayEnd.wasReopened')}
                            </div>
                          )}
                          {canReopen && (
                            <button
                              type="button"
                              className="ims-btn ims-btn-secondary"
                              style={{ padding: '3px 8px', fontSize: 11, marginTop: 4 }}
                              onClick={() => handleReopen(closed)}
                              disabled={working}
                            >
                              {t('dayEnd.reopen')}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </Layout>
  );
};

export default DayEndPage;
